import React, { useState, useMemo } from 'react'
import Modal from '@mui/material/Modal'
import Backdrop from '@mui/material/Backdrop'
import Fade from '@mui/material/Fade'
import Box from '@mui/material/Box'
import Checkbox from '@mui/material/Checkbox'
import CircularProgress from '@mui/material/CircularProgress'
import { alpha, styled } from '@mui/material/styles'
import InputBase from '@mui/material/InputBase'
import SearchIcon from '@mui/icons-material/Search'
import { useQuery } from '@tanstack/react-query'
import { fetchSubAdminsForGroupModal, SubAdminForModal } from '@/services/index'
import useAddSubAdminParticipants from '@/services/hooks/useAddSubAdminParticipants'
import DummyImage from '@/assets/dummy-profile-image.svg'
import close_icon from '@/assets/close_icon.svg'
import { GroupProps, profileType } from './GroupList'

type Props = {
  open: boolean
  groupInfo: GroupProps
  onClose: () => void
  onSuccess: (newProfiles: profileType[]) => void
}

const SearchWrapper = styled('div')(({ theme }) => ({
  display: 'flex',
  position: 'relative',
  borderRadius: theme.shape.borderRadius,
  backgroundColor: alpha(theme.palette.common.white, 0.08),
  '&:hover': { backgroundColor: alpha(theme.palette.common.white, 0.12) },
  width: '100%',
}))

const SearchIconWrapper = styled('div')(({ theme }) => ({
  padding: theme.spacing(0, 2),
  height: '100%',
  position: 'absolute',
  pointerEvents: 'none',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
}))

const StyledInputBase = styled(InputBase)(({ theme }) => ({
  color: 'inherit',
  width: '100%',
  '& .MuiInputBase-input': {
    padding: theme.spacing(1, 1, 1, 0),
    paddingLeft: `calc(1em + ${theme.spacing(4)})`,
  },
}))

const boxStyle = {
  position: 'absolute' as const,
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: { xs: 'calc(100% - 24px)', sm: '90%', md: 520 },
  maxWidth: 520,
  maxHeight: { xs: '92vh', sm: '85vh' },
  outline: 'none',
  display: 'flex',
  flexDirection: 'column' as const,
  color: 'white',
  border: '1px solid rgb(75, 90, 135)',
  borderRadius: '20px',
  background: 'rgba(69, 68, 68, 0)',
  backdropFilter: 'blur(28px)',
  padding: { xs: '16px 12px', md: '22px 16px' },
  overflow: 'hidden',
}

const AddParticipantModal: React.FC<Props> = ({
  open,
  groupInfo,
  onClose,
  onSuccess,
}) => {
  const [search, setSearch] = useState('')
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set())
  const [errorMsg, setErrorMsg] = useState('')

  const existingEmails = useMemo(
    () => new Set(groupInfo.profileIds.map((p) => p.userId?.email ?? '')),
    [groupInfo.profileIds],
  )

  const { data, isLoading: isFetchingSubAdmins } = useQuery({
    queryKey: ['sub-admins-for-group-modal'],
    queryFn: fetchSubAdminsForGroupModal,
    enabled: open,
  })

  const allSubAdmins: SubAdminForModal[] =
    (data as unknown as { data: { subAdminProfiles: SubAdminForModal[] } })
      ?.data?.subAdminProfiles ?? []

  /* Only show sub-admins not yet in the group */
  const availableSubAdmins = useMemo(
    () => allSubAdmins.filter((sa) => !existingEmails.has(sa.email)),
    [allSubAdmins, existingEmails],
  )

  const filteredSubAdmins = useMemo(() => {
    const term = search.trim().toLowerCase().replace(/\s+/g, '')
    if (!term) return availableSubAdmins
    return availableSubAdmins.filter((sa) => {
      const fullName =
        `${sa.profileInfo.firstName}${sa.profileInfo.lastName}`.toLowerCase()
      return fullName.includes(term) || sa.email.toLowerCase().includes(term)
    })
  }, [availableSubAdmins, search])

  const { mutate, isPending } = useAddSubAdminParticipants()

  const toggleSelect = (userId: string): void => {
    setSelectedIds((prev) => {
      const next = new Set(prev)
      if (next.has(userId)) {
        next.delete(userId)
      } else {
        next.add(userId)
      }
      return next
    })
  }

  const handleAdd = (): void => {
    setErrorMsg('')
    if (selectedIds.size === 0) {
      setErrorMsg('Please select at least one sub-admin.')
      return
    }

    mutate(
      { groupId: groupInfo._id, subAdminUserIds: Array.from(selectedIds) },
      {
        onSuccess: () => {
          const addedSubAdmins = allSubAdmins.filter((sa) =>
            selectedIds.has(sa._id),
          )
          const newProfiles: profileType[] = addedSubAdmins.map((sa) => ({
            countryCode: sa.profileInfo.countryCode ?? '+1',
            createdAt: new Date().toISOString(),
            firstName: sa.profileInfo.firstName,
            idBack: '',
            idBackOriginalName: '',
            idBackkey: '',
            idFront: '',
            idFrontKey: '',
            idFrontOriginalName: '',
            lastName: sa.profileInfo.lastName,
            phone: String(sa.profileInfo.phone ?? ''),
            profilePic: sa.profileInfo.profilePic ?? '',
            profilePicKey: '',
            profilePicOriginalName: '',
            userId: { _id: sa._id, email: sa.email, role: sa.role },
          }))
          onSuccess(newProfiles)
          handleClose()
        },
        onError: (err: unknown) => {
          const msg =
            (err as { message?: string })?.message ??
            'Failed to add participants. Please try again.'
          setErrorMsg(msg)
        },
      },
    )
  }

  const handleClose = (): void => {
    setSearch('')
    setSelectedIds(new Set())
    setErrorMsg('')
    onClose()
  }

  return (
    <Modal
      open={open}
      onClose={handleClose}
      closeAfterTransition
      slots={{ backdrop: Backdrop }}
      slotProps={{ backdrop: { timeout: 400 } }}
    >
      <Fade in={open}>
        <Box sx={boxStyle}>
          {/* Header */}
          <div className="flex items-start justify-between mb-4">
            <div className="flex-1" />
            <p className="flex-1 text-center text-[22px] font-bold">
              Add Participant
            </p>
            <div className="flex-1 flex justify-end">
              <div
                className="w-fit h-fit p-[3px] cursor-pointer ml-auto"
                onClick={handleClose}
              >
                <img
                  src={close_icon}
                  alt="Close"
                  className="bg-[#ffffff1a] rounded-full"
                />
              </div>
            </div>
          </div>

          {/* Group name context */}
          <p className="px-2 pb-3 text-[#959595] text-sm capitalize">
            Group:{' '}
            <span className="text-white font-medium">
              {groupInfo.groupName}
            </span>
          </p>

          {/* Search */}
          <div className="px-2 pb-4">
            <SearchWrapper>
              <SearchIconWrapper>
                <SearchIcon sx={{ color: '#959595', fontSize: 20 }} />
              </SearchIconWrapper>
              <StyledInputBase
                placeholder="Search sub-admins…"
                inputProps={{ 'aria-label': 'search sub-admins' }}
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                sx={{ color: 'white' }}
              />
            </SearchWrapper>
          </div>

          {/* Sub-admin list */}
          <div className="overflow-y-auto flex-1 max-h-[60vh] sm:max-h-[380px] px-2 pb-2">
            {isFetchingSubAdmins ? (
              <div className="flex justify-center py-10">
                <CircularProgress sx={{ color: '#FB5012' }} />
              </div>
            ) : filteredSubAdmins.length === 0 ? (
              <p className="text-center text-[#959595] py-8 text-sm">
                {availableSubAdmins.length === 0
                  ? 'All sub-admins are already in this group.'
                  : 'No sub-admins match your search.'}
              </p>
            ) : (
              filteredSubAdmins.map((sa, idx) => {
                const isSelected = selectedIds.has(sa._id)
                const isLast = idx === filteredSubAdmins.length - 1

                return (
                  <div
                    key={sa._id}
                    onClick={() => toggleSelect(sa._id)}
                    className={`flex items-center gap-4 py-4 ${isLast ? '' : 'border-b border-white/10'} cursor-pointer hover:bg-white/5 rounded-lg px-2 transition-colors`}
                  >
                    <img
                      src={
                        sa.profileInfo.profilePic
                          ? sa.profileInfo.profilePic
                          : DummyImage
                      }
                      alt={sa.profileInfo.firstName}
                      className="w-[40px] h-[40px] sm:w-[52px] sm:h-[52px] object-cover rounded-full border-2 border-white/30 flex-shrink-0"
                    />
                    <div className="flex flex-col flex-1 min-w-0">
                      <p className="text-white text-base font-medium truncate">
                        {sa.profileInfo.firstName} {sa.profileInfo.lastName}
                      </p>
                      <p className="text-[#959595] text-sm truncate">
                        {sa.email}
                      </p>
                    </div>
                    <Checkbox
                      checked={isSelected}
                      onChange={() => toggleSelect(sa._id)}
                      onClick={(e) => e.stopPropagation()}
                      sx={{
                        color: 'rgba(255,255,255,0.3)',
                        '&.Mui-checked': { color: '#FB5012' },
                        flexShrink: 0,
                      }}
                    />
                  </div>
                )
              })
            )}
          </div>

          {/* Error */}
          {errorMsg && (
            <p className="px-2 pt-2 text-[#FB5012] text-sm">{errorMsg}</p>
          )}

          {/* Footer */}
          <div className="px-2 pt-4 flex items-center justify-between gap-4 border-t border-white/10">
            <p className="text-[#959595] text-sm">
              {selectedIds.size > 0
                ? `${selectedIds.size} selected`
                : 'Select sub-admins to add'}
            </p>
            <button
              onClick={handleAdd}
              disabled={isPending || selectedIds.size === 0}
              className="flex items-center justify-center gap-2 bg-[#FB5012] text-white px-7 py-2.5 rounded-full text-lg capitalize disabled:opacity-40 hover:opacity-90 transition-opacity"
            >
              {isPending ? (
                <CircularProgress size={18} sx={{ color: '#fff' }} />
              ) : null}
              {isPending ? 'Adding…' : 'Add Selected'}
            </button>
          </div>
        </Box>
      </Fade>
    </Modal>
  )
}

export { AddParticipantModal }
